import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import RecordService from './RecordService';
import MyTransactions from './MyTransactions';
import ClockInOut from './ClockInOut';
import JourneyPlan from './JourneyPlan';
import OutletRegistration from './OutletRegistration';
import SparesUsed from './SparesUsed';
import EquipmentRegistration from './EquipmentRegistration';
import TechnicianNotifications from './TechnicianNotifications';
import RequestTransfer from './RequestTransfer';
import GPSPermissionModal from '../components/GPSPermissionModal';
import SettingsModule from '../components/SettingsModule';
import { startLocationTracking, stopLocationTracking, formatCoordinates, openInGoogleMaps } from '../utils/enhancedLocationTracker';
import { ROLES, isSuperAdmin, isAdminOrHigher } from '../services/authService';
import './Dashboard.css';
import {
  FaStore, FaFaucet, FaBeer, FaExchangeAlt, FaRoute, FaClipboardCheck,
  FaWrench, FaHistory, FaClock, FaBell, FaCog, FaInfoCircle, FaMapMarkerAlt,
  FaSignOutAlt, FaStar, FaCrown, FaHome, FaChartBar, FaPhone,
} from 'react-icons/fa';

function Dashboard({ user, onLogout, onOpenSupervisorDash, onUserUpdate }) {
  const [activePage, setActivePage]         = useState('dashboard');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [unreadCount, setUnreadCount]       = useState(0);
  const [gpsGranted, setGpsGranted]         = useState(false);

  // ── GPS Permission & Tracking ─────────────────────────────────────────────
  const handleLocationGranted = (coords) => {
    setCurrentLocation(coords);
    setGpsGranted(true);
    // Start background tracking (logs every 5 minutes)
    startLocationTracking(user);
  };

  // Stop tracking on unmount
  useEffect(() => {
    return () => {
      stopLocationTracking();
    };
  }, []);

  // Update current location display every 5 seconds
  useEffect(() => {
    if (!gpsGranted) return;
    
    const updateLocation = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date()
          });
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000 }
      );
    };

    const interval = setInterval(updateLocation, 5000);
    return () => clearInterval(interval);
  }, [gpsGranted]);

  // ── Real-time unread notification count ───────────────────────────────────
  useEffect(() => {
    const q = query(
      collection(db, 'technicianNotifications'),
      where('recipientId', '==', user.id),
      where('read', '==', false)
    );
    const unsub = onSnapshot(q, (snap) => {
      setUnreadCount(snap.size);
    });
    return () => unsub();
  }, [user.id]);

  // ✅ UPDATED: Admin and Super Admin can access ALL modules
  const modules = [
    // Equipment & Outlets - Everyone
    { id: 'outlets',       name: 'Outlets',         icon: <FaStore />, color: '#2563eb', description: 'Register outlets',     role: ['technician', 'supervisor', 'admin', 'super_admin'], page: 'outlets'       },
    { id: 'keg-pump',      name: 'Keg Pump',         icon: <FaFaucet />, color: '#0891b2', description: 'Register keg pumps',   role: ['technician', 'supervisor', 'admin', 'super_admin'], page: 'keg-pump'      },
    { id: 'draft-machine', name: 'Draft Machine',    icon: <FaBeer />, color: '#d97706', description: 'Register draft machines', role: ['technician', 'supervisor', 'admin', 'super_admin'], page: 'draft-machine' },
    { id: 'equipment-transfer', name: 'Equipment Transfer', icon: <FaExchangeAlt />, color: '#7c3aed', description: 'Move equipment between outlets', role: ['technician', 'supervisor', 'admin', 'super_admin'], page: 'equipment-transfer' },
    
    // ✅ FIXED: Admins can now access these (for direct visibility)
    { id: 'journey-plan',  name: 'Journey Plan',     icon: <FaRoute />, color: '#059669', description: 'Plan daily visits',    role: ['technician', 'supervisor', 'admin', 'super_admin'], page: 'journey-plan'  },
    { id: 'record-service',name: 'Record Service',   icon: <FaClipboardCheck />, color: '#dc2626', description: 'Log service work',     role: ['technician'], page: 'record-service'}, // ✅ ONLY technicians
    { id: 'spares-used',   name: 'Spares Used',      icon: <FaWrench />, color: '#ea580c', description: 'Track spare parts',    role: ['technician', 'supervisor', 'admin', 'super_admin'], page: 'spares-used'   },
    { id: 'transactions',  name: 'Transactions',     icon: <FaHistory />, color: '#0d9488', description: 'View service history', role: ['technician', 'supervisor', 'admin', 'super_admin'], page: 'transactions'  }, // ✅ Changed from "My Transactions"
    { id: 'clock',         name: 'Clock',            icon: <FaClock />, color: '#4f46e5', description: 'Clock in/out',         role: ['technician', 'admin', 'super_admin'], page: 'clock'         }, // ✅ Admins can clock too
    
    // Notifications - Everyone
    { id: 'notifications', name: 'Notifications',    icon: <FaBell />, color: '#ca8a04', description: 'View alerts',          role: ['technician', 'supervisor', 'admin', 'super_admin'], page: 'notifications' },

    // Settings - Everyone
    { id: 'settings',       name: 'Settings',         icon: <FaCog />, color: '#475569', description: 'Account & notifications', role: ['technician', 'supervisor', 'admin', 'super_admin'], page: 'settings' },

    // Help
    { id: 'faqs',          name: 'FAQs',             icon: <FaInfoCircle />, color: '#0284c7', description: 'Help & support',       role: ['technician', 'supervisor', 'admin', 'super_admin'], page: 'faqs'          }
  ];

  const visibleModules = modules.filter(m => m.role.includes(user.role));

  const handleModuleClick = (page) => {
    if      (page === 'record-service')      setActivePage('record-service');
    else if (page === 'transactions')        setActivePage('transactions');
    else if (page === 'clock')               setActivePage('clock');
    else if (page === 'journey-plan')        setActivePage('journey-plan');
    else if (page === 'outlets')             setActivePage('outlets');
    else if (page === 'spares-used')         setActivePage('spares-used');
    else if (page === 'keg-pump')            setActivePage('keg-pump');
    else if (page === 'draft-machine')       setActivePage('draft-machine');
    else if (page === 'equipment-transfer')  setActivePage('equipment-transfer');
    else if (page === 'notifications')       setActivePage('notifications');
    else if (page === 'settings')            setActivePage('settings');
    else if (page === 'faqs')                showToast('FAQs coming soon...');
    else showToast(`${page} coming soon...`);
  };

  const handleBottomNavClick = (navItem) => {
    if      (navItem === 'home')                    setActivePage('dashboard');
    else if (navItem === 'notifications')           setActivePage('notifications');
    else if (navItem === 'supervisor-dashboard')    onOpenSupervisorDash();
    else if (navItem === 'contact')                 showToast('Contact support: +254 707767820');
  };

  const TOAST_ICON_SVG = '<svg viewBox="0 0 512 512" width="18" height="18" fill="currentColor"><path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm0 110c23.196 0 42 18.804 42 42s-18.804 42-42 42-42-18.804-42-42 18.804-42 42-42zm56 254c0 6.627-5.373 12-12 12h-88c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h12v-64h-12c-6.627 0-12-5.373-12-12v-24c0-6.627 5.373-12 12-12h64c6.627 0 12 5.373 12 12v100h12c6.627 0 12 5.373 12 12v24z"/></svg>';

  const showToast = (message) => {
    document.querySelectorAll('.toast-notification').forEach(t => t.remove());
    const toast = document.createElement('div');
    toast.className = 'toast-notification';
    toast.innerHTML = `<div class="toast-icon">${TOAST_ICON_SVG}</div><div class="toast-message">${message}</div>`;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  };

  // ── Page renders ──────────────────────────────────────────────────────────
  if (activePage === 'record-service')
    return <RecordService user={user} onBack={() => setActivePage('dashboard')} />;

  if (activePage === 'transactions')
    return <MyTransactions user={user} onBack={() => setActivePage('dashboard')} />;

  if (activePage === 'clock')
    return <ClockInOut user={user} onBack={() => setActivePage('dashboard')} />;

  if (activePage === 'journey-plan')
    return <JourneyPlan user={user} onBack={() => setActivePage('dashboard')} />;

  if (activePage === 'outlets')
    return <OutletRegistration user={user} onBack={() => setActivePage('dashboard')} />;

  if (activePage === 'spares-used')
    return <SparesUsed user={user} onBack={() => setActivePage('dashboard')} />;

  if (activePage === 'notifications')
    return <TechnicianNotifications user={user} onBack={() => setActivePage('dashboard')} />;

  if (activePage === 'settings')
    return <SettingsModule user={user} onBack={() => setActivePage('dashboard')} onUserUpdate={onUserUpdate} />;

  if (activePage === 'keg-pump')
    return <EquipmentRegistration user={user} equipmentType="keg_pump" onBack={() => setActivePage('dashboard')} />;

  if (activePage === 'draft-machine')
    return <EquipmentRegistration user={user} equipmentType="draft_machine" onBack={() => setActivePage('dashboard')} />;

  if (activePage === 'equipment-transfer')
    return <RequestTransfer user={user} onBack={() => setActivePage('dashboard')} />;

  // ── Dashboard home ────────────────────────────────────────────────────────
  return (
    <div className="dashboard">
      {/* ✅ GPS Permission Modal - Blocks access until granted */}
      {!gpsGranted && (
        <GPSPermissionModal onLocationGranted={handleLocationGranted} />
      )}

      {/* ✅ GPS Coordinates Bar - Top of header (desktop only) */}
      {currentLocation && (
        <div className="gps-coordinates-bar">
          <div className="gps-coords-content">
            <span className="gps-icon"><FaMapMarkerAlt /></span>
            <span className="gps-coords-text">
              {formatCoordinates(currentLocation.latitude, currentLocation.longitude)}
            </span>
            <span className="gps-accuracy">±{Math.round(currentLocation.accuracy)}m</span>
            <button 
              onClick={() => openInGoogleMaps(currentLocation.latitude, currentLocation.longitude)}
              className="gps-open-map-btn"
              title="Open in Google Maps"
            >
              <FaMapMarkerAlt />
            </button>
          </div>
        </div>
      )}

      <header className="dashboard-header">
        {/* ✅ LEFT: Logo */}
        <div className="header-left">
          <div className="logo">
            <div className="dilex-logo-container">
              <div className="header-logo-badge">
                <img src="/dionysus-logo.png" alt="Dionysus" className="header-logo-img" />
              </div>
            </div>
          </div>
        </div>

        {/* ✅ CENTER: Online Status */}
        <div className="header-center">
          <div className="status-indicator online">
            <span className="status-dot"></span>
            Online
          </div>
        </div>

        {/* ✅ RIGHT: Settings + Logout */}
        <div className="header-right">
          <button onClick={() => setActivePage('settings')} className="logout-btn" title="Settings"><FaCog /></button>
          <button onClick={onLogout} className="logout-btn" title="Logout"><FaSignOutAlt /></button>
        </div>
      </header>

      <main className="dashboard-content">
        <div className="dashboard-welcome">
          <h1>Welcome back, {user.name.split(' ')[0]}!</h1>
          <p>Select a module to get started</p>
          
          {/* Role badge */}
          {isSuperAdmin(user) && (
            <div style={{
              marginTop: 12,
              padding: '8px 14px',
              background: 'rgba(168,85,247,0.1)',
              border: '1px solid rgba(168,85,247,0.3)',
              borderRadius: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              fontWeight: 600,
              color: '#c084fc'
            }}>
              You have full system control
            </div>
          )}
          
          {isAdminOrHigher(user) && !isSuperAdmin(user) && (
            <div style={{
              marginTop: 12,
              padding: '8px 14px',
              background: 'rgba(245,158,11,0.1)',
              border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              fontWeight: 600,
              color: '#fcd34d'
            }}>
              <FaCrown /> Admin Access - Full Visibility & Monitoring
            </div>
          )}

          {/* Unread alert banner */}
          {unreadCount > 0 && (
            <div
              style={{
                marginTop:    16,
                padding:      '10px 16px',
                background:   'rgba(171,45,42,0.08)',
                border:       '1px solid rgba(171,45,42,0.2)',
                borderRadius: 10,
                display:      'flex',
                alignItems:   'center',
                gap:          10,
                cursor:       'pointer'
              }}
              onClick={() => setActivePage('notifications')}
            >
              <span style={{ fontSize: 20 }}><FaBell /></span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#AB2D2A' }}>
                You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''} from your supervisor
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 13, color: '#AB2D2A' }}>View →</span>
            </div>
          )}
        </div>

        <div className="modules-grid">
          {visibleModules.map((module) => (
            <div
              key={module.id}
              className="module-card"
              onClick={() => handleModuleClick(module.page)}
              tabIndex={0}
              role="button"
              aria-label={`Open ${module.name}`}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleModuleClick(module.page);
                }
              }}
              style={{ position: 'relative' }}
            >
              {/* Unread badge on notifications card */}
              {module.id === 'notifications' && unreadCount > 0 && (
                <div style={{
                  position:     'absolute',
                  top:          -6,
                  right:        -6,
                  background:   '#AB2D2A',
                  color:        '#fff',
                  fontSize:     10,
                  fontWeight:   700,
                  minWidth:     20,
                  height:       20,
                  borderRadius: 10,
                  display:      'flex',
                  alignItems:   'center',
                  justifyContent: 'center',
                  padding:      '0 5px',
                  border:       '2px solid #fff',
                  zIndex:       2
                }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </div>
              )}

              {/* Role badge on restricted modules */}
              {(module.id === 'user-management' || (module.id === 'supervisor-monitor' && !isSuperAdmin(user))) && (
                <div style={{
                  position:     'absolute',
                  top:          -6,
                  left:         -6,
                  background:   module.id === 'user-management' ? '#a855f7' : '#f59e0b',
                  color:        '#fff',
                  fontSize:     9,
                  fontWeight:   700,
                  padding:      '2px 8px',
                  borderRadius: 10,
                  border:       '2px solid #fff',
                  zIndex:       2
                }}>
                  {module.id === 'user-management' ? <FaStar /> : <FaCrown />} {module.id === 'user-management' ? 'SUPER' : 'ADMIN'}
                </div>
              )}

              <div className="module-icon" style={{ color: module.color, background: `${module.color}1a` }}>{module.icon}</div>
              <h3 className="module-name">{module.name}</h3>
              <p className="module-description">{module.description}</p>
            </div>
          ))}
        </div>
      </main>

      <nav className="bottom-nav">
        <button
          className={`nav-item ${activePage === 'dashboard' ? 'active' : ''}`}
          onClick={() => handleBottomNavClick('home')}
          aria-label="Home"
        >
          <span className="nav-icon"><FaHome /></span>
          <span className="nav-label">Home</span>
        </button>

        {/* Notifications shortcut in bottom nav — shows badge */}
        <button
          className={`nav-item ${activePage === 'notifications' ? 'active' : ''}`}
          onClick={() => handleBottomNavClick('notifications')}
          aria-label="Notifications"
          style={{ position: 'relative' }}
        >
          {unreadCount > 0 && (
            <span style={{
              position:     'absolute',
              top:          4,
              right:        10,
              background:   '#AB2D2A',
              color:        '#fff',
              fontSize:     9,
              fontWeight:   700,
              minWidth:     16,
              height:       16,
              borderRadius: 8,
              display:      'flex',
              alignItems:   'center',
              justifyContent: 'center',
              padding:      '0 3px'
            }}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          <span className="nav-icon"><FaBell /></span>
          <span className="nav-label">Alerts</span>
        </button>

        {(user.role === ROLES.SUPERVISOR || isAdminOrHigher(user)) && (
          <button
            className="nav-item"
            onClick={() => handleBottomNavClick('supervisor-dashboard')}
            aria-label="Supervisor Dashboard"
          >
            <span className="nav-icon"><FaChartBar /></span>
            <span className="nav-label">Dashboard</span>
          </button>
        )}

        <button
          className="nav-item"
          onClick={() => handleBottomNavClick('contact')}
          aria-label="Contact Support"
        >
          <span className="nav-icon"><FaPhone /></span>
          <span className="nav-label">Contact</span>
        </button>
      </nav>
    </div>
  );
}

export default Dashboard;
